/**
 * SuperAdminOrgs — Multi-tenant control tower for platform operators.
 * Visible only to accounts in SUPER_ADMIN_EMAILS (backend enforces).
 *
 * Row: one organisation with users/projects counts + Stripe MRR estimate.
 * Detail drawer: users, projects, audit trail, Impersonate + Suspend actions.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Building2, Users, FolderKanban, DollarSign, Search, X, Shield,
  UserRoundCog, Ban, CircleCheck, ArrowUpRight, Clock, FileText, Loader2,
} from "lucide-react";
import client from "../lib/apiClient";
import { useCSuiteAuth } from "../state/CSuiteAuthContext";
import { useNavigate } from "react-router-dom";

const fmt = (n) => typeof n === "number" ? n.toLocaleString() : (n ?? "—");
const money = (n) => `$${(n || 0).toLocaleString()}`;
const dt = (s) => {
  if (!s) return "—";
  try { return new Date(s).toISOString().slice(0, 10); } catch { return "—"; }
};

function KpiTile({ label, value, sub, icon: Icon }) {
  return (
    <div className="p-4 border border-[var(--border)] rounded-sm bg-white flex items-start gap-3">
      <div className="w-9 h-9 rounded-sm grid place-items-center shrink-0"
           style={{ background: "#4A0F1A", color: "#FDF2F4" }}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
        <div className="font-display text-2xl font-extrabold tracking-tight text-[var(--navy)] leading-tight mt-0.5">{value}</div>
        {sub && <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function SuperAdminOrgs() {
  const { user, loading: authLoading } = useCSuiteAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await client.get(`/admin/orgs${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRows(data.orgs || []); setTotals(data.totals || null);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 403) setErr("Super-admin only. Ask an admin to add you to SUPER_ADMIN_EMAILS.");
      else setErr(e?.response?.data?.detail || e.message || "Failed to load orgs");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (!authLoading && user) load(); /* eslint-disable-next-line */ }, [authLoading, user]);

  const openOrg = async (org) => {
    setSelected(org); setDetail(null);
    try {
      const { data } = await client.get(`/admin/orgs/${org.id}`);
      setDetail(data);
    } catch (e) { setDetail({ error: e?.response?.data?.detail || e.message }); }
  };

  const suspend = async (org, on) => {
    if (!window.confirm(on ? `Suspend "${org.name}"? Their team will lose access.` : `Reactivate "${org.name}"?`)) return;
    setBusyId(org.id);
    try {
      await client.post(`/admin/orgs/${org.id}/suspend`, { suspend: on });
      await load();
      if (selected?.id === org.id) await openOrg(org);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusyId(null); }
  };

  const impersonate = async (org) => {
    if (!window.confirm(`Log in as a CIO in "${org.name}"? A 30-minute audit-logged session will open in a new tab.`)) return;
    setBusyId(org.id);
    try {
      const { data } = await client.post(`/admin/orgs/${org.id}/impersonate`);
      // Open a fresh tab, seed the impersonation token in its localStorage, then land on the C-Suite dashboard.
      const w = window.open("/csuite/dashboard", "_blank");
      // Use postMessage-free hand-off: set token synchronously before landing page mounts.
      try {
        setTimeout(() => {
          try {
            w?.localStorage?.setItem("pmv5_csuite_token", data.token);
            w?.location?.replace("/csuite/dashboard");
          } catch { /* Cross-origin — user must paste manually */ }
        }, 300);
      } catch { /* noop */ }
      alert(`Impersonation token issued for ${data.target_email}. Expires in ${data.expires_minutes} minutes.`);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusyId(null); }
  };

  const filtered = useMemo(() => rows, [rows]);

  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6">
        <div className="text-sm text-zinc-600">Please <button className="underline" onClick={() => nav("/csuite")}>log in</button> to continue.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] p-6 md:p-10" data-testid="super-admin-orgs">
      {/* Header */}
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A1F2B]">
            Super-Admin  ·  Platform Control Tower
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[var(--navy)] mt-1">
            Organizations
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Every tenant on Capittance. One row = one organization. Drill in for users, projects, audit trail, impersonation and suspension.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              className="fld pl-8 pr-3 py-2 w-64"
              placeholder="Search org name / slug / domain…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="super-admin-search"
            />
          </div>
          <button className="btn btn-secondary btn-sm" onClick={load} data-testid="super-admin-refresh">Refresh</button>
        </div>
      </div>

      {/* KPIs */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="super-admin-totals">
          <KpiTile label="Organizations" value={fmt(totals.orgs)}      icon={Building2}    sub={`${totals.paid_orgs} paid · ${totals.trial_orgs} trial`} />
          <KpiTile label="Users total"   value={fmt(totals.users)}     icon={Users}        sub="Across all tenants" />
          <KpiTile label="Projects"      value={fmt(totals.projects)}  icon={FolderKanban} sub="Total in DB" />
          <KpiTile label="Est. MRR"      value={money(totals.mrr)}     icon={DollarSign}   sub="Active subs × $350" />
          <KpiTile label="Signed in as"  value={user.email?.split("@")[0] || "you"} icon={Shield} sub="Super-admin" />
        </div>
      )}

      {/* Errors */}
      {err && <div className="mb-4 p-3 rounded-sm border border-red-200 bg-red-50 text-sm text-red-800" data-testid="super-admin-error">{err}</div>}

      {/* Table */}
      <div className="bg-white border border-[var(--border)] rounded-sm overflow-hidden">
        <table className="tbl w-full">
          <thead>
            <tr>
              <th>Organization</th>
              <th className="w-28">Slug</th>
              <th className="w-40">Domain</th>
              <th className="w-20">Plan</th>
              <th className="w-16 text-right">Users</th>
              <th className="w-20 text-right">Projects</th>
              <th className="w-28">Created</th>
              <th className="w-24">Status</th>
              <th className="w-56 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-8"><Loader2 size={16} className="inline animate-spin text-zinc-400" /></td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-zinc-400 italic">No organizations match.</td></tr>
            )}
            {!loading && filtered.map((org) => (
              <tr key={org.id} className="hover:bg-[var(--surface)] cursor-pointer" data-testid={`org-row-${org.slug}`} onClick={() => openOrg(org)}>
                <td className="font-semibold text-[var(--navy)]">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-sm grid place-items-center shrink-0 text-white text-xs font-bold"
                          style={{ background: "#4A0F1A" }}>
                      {(org.name || "?").slice(0, 2).toUpperCase()}
                    </span>
                    <span className="truncate">{org.name}</span>
                  </div>
                </td>
                <td className="font-mono text-[11px] text-zinc-500">{org.slug}</td>
                <td className="font-mono text-[11px] text-zinc-500 truncate">{org.domain || "—"}</td>
                <td>
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider"
                        style={{ background: "#F1EBFE", color: "#4C1D95" }}>
                    {org.plan || "starter"}
                  </span>
                </td>
                <td className="text-right font-mono">{org.users_count}</td>
                <td className="text-right font-mono">{org.projects_count}</td>
                <td className="font-mono text-[11px] text-zinc-500">{dt(org.created_at)}</td>
                <td>
                  {org.suspended ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-red-700 font-mono"><Ban size={11}/> suspended</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] text-green-700 font-mono"><CircleCheck size={11}/> active</span>
                  )}
                </td>
                <td className="text-right">
                  <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-xs" onClick={() => impersonate(org)} disabled={busyId === org.id || org.slug === "legacy"} title="Log in as a CIO in this org (30-min audited session)" data-testid={`btn-impersonate-${org.slug}`}>
                      <UserRoundCog size={11}/> Impersonate
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      style={{ color: org.suspended ? "#16A34A" : "#DC2626" }}
                      onClick={() => suspend(org, !org.suspended)}
                      disabled={busyId === org.id || org.slug === "legacy"}
                      data-testid={`btn-suspend-${org.slug}`}
                    >
                      {org.suspended ? <><CircleCheck size={11}/> Reactivate</> : <><Ban size={11}/> Suspend</>}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setSelected(null)}>
          <div
            className="w-full max-w-2xl bg-[#FDFBFB] h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="org-detail-drawer"
          >
            <div className="px-6 py-5 border-b border-[var(--border)] flex items-start justify-between" style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 100%)", color: "#FDF2F4" }}>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "#E5A9B3" }}>
                  Organization detail  ·  {selected.slug}
                </div>
                <div className="font-display text-2xl font-extrabold mt-1">{selected.name}</div>
                <div className="font-mono text-[11px] mt-1" style={{ color: "#F0C7CE" }}>
                  {selected.domain || "no domain"}  ·  {fmt(selected.users_count)} users  ·  {fmt(selected.projects_count)} projects  ·  since {dt(selected.created_at)}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-white/10 rounded-sm" data-testid="btn-close-drawer">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {!detail && <div className="text-center py-8"><Loader2 size={18} className="inline animate-spin text-zinc-400" /></div>}
              {detail?.error && <div className="p-3 rounded-sm bg-red-50 border border-red-200 text-red-800 text-sm">{detail.error}</div>}

              {detail?.users && (
                <section>
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#7A1F2B] mb-2 flex items-center gap-1.5">
                    <Users size={11}/> Users · {detail.users.length}
                  </div>
                  <div className="space-y-1.5">
                    {detail.users.map((u) => (
                      <div key={u.email} className="flex items-center justify-between text-[12px] p-2 rounded-sm bg-white border border-[var(--border)]" data-testid={`user-${u.email}`}>
                        <div className="min-w-0">
                          <div className="font-semibold text-[var(--navy)] truncate">{u.username || u.email}</div>
                          <div className="font-mono text-[10px] text-zinc-500 truncate">{u.email}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1EBFE] text-[#4C1D95]">{u.role}</span>
                          {u.org_role && u.org_role !== "Member" && (
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800">{u.org_role}</span>
                          )}
                          {u.subscription?.status === "active" && (
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-green-100 text-green-800">paid</span>
                          )}
                          {!u.verified && (
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600">unverified</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {detail?.projects && (
                <section>
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#7A1F2B] mb-2 flex items-center gap-1.5">
                    <FolderKanban size={11}/> Projects · {detail.projects.length}
                  </div>
                  <div className="space-y-1.5">
                    {detail.projects.length === 0 && <div className="text-[11px] text-zinc-400 italic">No projects yet.</div>}
                    {detail.projects.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-[12px] p-2 rounded-sm bg-white border border-[var(--border)]" data-testid={`proj-${p.id}`}>
                        <div className="min-w-0">
                          <div className="font-semibold text-[var(--navy)] truncate">{p.name}</div>
                          <div className="font-mono text-[10px] text-zinc-500 truncate">{p.phase} · {p.pm || "no PM"} · updated {dt(p.updatedAt)}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono text-[10px] text-zinc-600">{money(p.budget)}</span>
                          <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm"
                                style={{
                                  background: p.health === "On Track" ? "#DCFCE7" : p.health === "At Risk" ? "#FEF3C7" : p.health === "Off Track" ? "#FEE2E2" : "#F4F4F5",
                                  color:      p.health === "On Track" ? "#166534" : p.health === "At Risk" ? "#92400E" : p.health === "Off Track" ? "#991B1B" : "#52525B",
                                }}>
                            {p.health || "—"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {detail?.audit && (
                <section>
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#7A1F2B] mb-2 flex items-center gap-1.5">
                    <Clock size={11}/> Audit trail (last 50)
                  </div>
                  <div className="space-y-1">
                    {detail.audit.length === 0 && <div className="text-[11px] text-zinc-400 italic">No recorded activity.</div>}
                    {detail.audit.map((a, i) => (
                      <div key={i} className="text-[11px] font-mono text-zinc-600 flex items-center gap-2 p-1.5 hover:bg-white rounded-sm">
                        <FileText size={10} className="text-zinc-400 shrink-0" />
                        <span className="text-zinc-400 w-24 shrink-0">{dt(a.at)}</span>
                        <span className="w-24 truncate shrink-0" title={a.action}>{a.action}</span>
                        <span className="w-48 truncate shrink-0 text-zinc-500" title={a.actor_email}>{a.actor_email}</span>
                        <span className="truncate text-zinc-500">{a.entity} · {a.entity_id?.slice(0, 8)}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <div className="pt-4 border-t border-[var(--border)] flex items-center gap-2 flex-wrap">
                <button className="btn btn-primary btn-sm" onClick={() => impersonate(selected)} disabled={selected.slug === "legacy"} data-testid="drawer-impersonate">
                  <UserRoundCog size={12}/> Open portal as tenant
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ color: selected.suspended ? "#16A34A" : "#DC2626" }}
                  onClick={() => suspend(selected, !selected.suspended)}
                  disabled={selected.slug === "legacy"}
                  data-testid="drawer-suspend"
                >
                  {selected.suspended ? <><CircleCheck size={12}/> Reactivate tenant</> : <><Ban size={12}/> Suspend tenant</>}
                </button>
                <div className="ml-auto text-[10px] font-mono text-zinc-400 flex items-center gap-1">
                  <ArrowUpRight size={10}/> All actions are audit-logged
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
